import { useQuery } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "./use-auth";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";

export interface RecentStudiedSession {
    id: string;
    topic: string;
    slug: string;
    created_at: string;
    updated_at: string;
    category: string | null;
    banner_url: string | null;
    user_id: string | null;
    total_seconds: number;
    creator_email?: string;
    creator_avatar_url?: string;
    creator_display_name?: string;
    view_count?: number;
    like_count?: number;
    comment_count?: number;
    bookmark_count?: number;
    questions?: any[];
    vocabulary?: any[];
}

interface UseRecentStudiedOptions {
    limit?: number;
    enabled?: boolean;
}

interface TopicTimeEntry {
    practice_session_id: string;
    total_seconds: number;
    updated_at: string;
}

/**
 * Hook to fetch recently studied topics based on topic_time tracking.
 * Returns topics ordered by when they were last studied (updated_at in topic_time table).
 */
export const useRecentStudied = (options: UseRecentStudiedOptions = {}) => {
    const { limit = 5, enabled = true } = options;
    const { userId, data: isAuthenticated } = useAuth();

    return useQuery({
        queryKey: queryKeys.recentStudied.list(userId, limit),
        queryFn: async (): Promise<RecentStudiedSession[]> => {
            if (!userId) return [];

            // Fetch topic_time entries ordered by updated_at (most recently studied first)
            const { data: topicTimeData, error: topicTimeError } = await db.from("topic_time" as "practice_sessions") // Cast to known table for type checking
                .select(`
                    practice_session_id,
                    total_seconds,
                    updated_at
                `)
                .eq("user_id", userId)
                .order("updated_at", { ascending: false })
                .limit(limit);

            if (topicTimeError) {
                console.error("Error fetching topic time:", topicTimeError);
                return [];
            }

            if (!topicTimeData || topicTimeData.length === 0) {
                return [];
            }

            // Cast to proper type since topic_time table types aren't regenerated
            const timeEntries = topicTimeData as unknown as TopicTimeEntry[];

            // Get session IDs
            const sessionIds = timeEntries.map((tt) => tt.practice_session_id);

            // Fetch practice sessions for these IDs
            const { data: sessionsData, error: sessionsError } = await db.from("practice_sessions")
                .select(`
                    id,
                    topic,
                    slug,
                    banner_url,
                    created_at,
                    user_id,
                    view_count,
                    like_count,
                    comment_count,
                    bookmark_count,
                    questions (id),
                    vocabulary (id),
                    categories!category_id (
                        name
                    )
                `)
                .in("id", sessionIds);

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
                return [];
            }

            // Create a map for easy lookup.
            // `questions (id)` / `vocabulary (id)` embed only the child keys; the
            // row is cast to any because those embeds aren't modelled by the
            // generated types. Counts come from `.length` below.
            const sessionsMap = new Map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (sessionsData as any[] | null)?.map((s: any) => [s.id, s]) ?? []
            );

            // Build the result, preserving the order from topic_time
            const result: RecentStudiedSession[] = [];

            for (const tt of timeEntries) {
                const session = sessionsMap.get(tt.practice_session_id);
                if (session) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const typedSession = session as any;
                    result.push({
                        id: session.id,
                        topic: session.topic,
                        slug: session.slug,
                        created_at: session.created_at,
                        updated_at: tt.updated_at,
                        category: (session.categories as { name: string } | null)?.name ?? null,
                        banner_url: (session as any).banner_url || null,
                        user_id: session.user_id,
                        total_seconds: tt.total_seconds,
                        view_count: (session as any).view_count || 0,
                        like_count: (session as any).like_count || 0,
                        comment_count: (session as any).comment_count || 0,
                        bookmark_count: (session as any).bookmark_count || 0,
                        // Count placeholders — list cards only render `.length`.
                        questions: Array.from({ length: typedSession.questions?.length ?? 0 }),
                        vocabulary: Array.from({ length: typedSession.vocabulary?.length ?? 0 }),
                    });
                }
            }

            // Fetch user data to get creator display names
            const userIds = [...new Set(result.filter(s => s.user_id).map(s => s.user_id!))];
            const userDataMap = await fetchUserDataBatch(userIds);

            // Add creator display name to each session
            const sessionsWithCreator = result.map((session) => {
                const userData = session.user_id ? userDataMap.get(session.user_id) : null;
                return {
                    ...session,
                    creator_email: userData?.email || "Unknown",
                    creator_avatar_url: userData?.avatar_url || null,
                    creator_display_name: userData?.display_name || "Unknown",
                };
            });

            return sessionsWithCreator;
        },
        enabled: !!isAuthenticated && !!userId && enabled,
        staleTime: 1000 * 60 * 2, // Cache for 2 minutes
        refetchOnWindowFocus: false,
        placeholderData: (previousData) => previousData,
    });
};
